import { useState, useEffect } from 'react';

import { RiveDumpster } from '@/components/RiveDumpster';

/**
 * Combined Dump Modal - Shows progress for both swap and burn operations
 * Supports batch signing for single signature prompt
 */

export type CombinedDumpStep =
  | 'preparing'      // Getting quotes, building transactions
  | 'signing'        // User signs both transactions at once
  | 'swapping'       // Sending/confirming swap transaction
  | 'burning'        // Sending/confirming burn transaction
  | 'complete';      // All done

export type OperationType = 'swap_only' | 'burn_only' | 'swap_and_burn';

interface CombinedDumpModalProps {
  isOpen: boolean;
  operationType: OperationType;
  currentStep: CombinedDumpStep;
  // Swap info
  swapTokenCount: number;
  swapCurrentIndex?: number;
  swapCurrentSymbol?: string;
  estimatedSwapSol?: number;
  // Burn info
  burnTokenCount: number;
  burnNftCount: number;
  estimatedBurnSol?: number;
  // Progress
  attemptCount?: number;
  maxAttempts?: number;
  isEmbeddedWallet?: boolean;
  // Error state
  error?: string;
}

export function CombinedDumpModal({
  isOpen,
  operationType,
  currentStep,
  swapTokenCount,
  swapCurrentIndex = 0,
  swapCurrentSymbol = '',
  estimatedSwapSol = 0,
  burnTokenCount,
  burnNftCount,
  estimatedBurnSol = 0,
  attemptCount = 0,
  maxAttempts = 20,
  isEmbeddedWallet = false,
  error,
}: CombinedDumpModalProps) {
  const [dots, setDots] = useState('');

  // Animate dots
  useEffect(() => {
    const interval = setInterval(() => {
      setDots(prev => (prev.length >= 3 ? '' : prev + '.'));
    }, 500);
    return () => clearInterval(interval);
  }, []);

  // Add shimmer animation styles
  useEffect(() => {
    if (!isOpen) return;

    const style = document.createElement('style');
    style.textContent = `
      @keyframes shimmer {
        0% { transform: translateX(-100%); }
        100% { transform: translateX(200%); }
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const totalBurnAssets = burnTokenCount + burnNftCount;
  const totalEstimatedSol = estimatedSwapSol + estimatedBurnSol;

  const getTitle = () => {
    switch (operationType) {
      case 'swap_only':
        return `SWAPPING ${swapTokenCount} TOKEN${swapTokenCount > 1 ? 'S' : ''}`;
      case 'burn_only':
        return `JUNKING ${totalBurnAssets} ASSET${totalBurnAssets > 1 ? 'S' : ''}`;
      case 'swap_and_burn':
        return 'JUNKING & SWAPPING';
      default:
        return 'PROCESSING';
    }
  };

  const getTitleColor = () => {
    switch (operationType) {
      case 'swap_only':
        return '#4ADE80'; // Green for swap
      case 'burn_only':
        return '#F97316'; // Orange for burn
      case 'swap_and_burn':
        return '#4ADE80'; // Green for combined
      default:
        return '#FFFFFF';
    }
  };

  const getStepMessage = () => {
    switch (currentStep) {
      case 'preparing':
        if (operationType === 'swap_and_burn') {
          return 'Building swap & burn transactions';
        } else if (operationType === 'swap_only') {
          return 'Getting best swap routes from Jupiter';
        } else {
          return 'Preparing burn transaction';
        }
      case 'signing':
        if (isEmbeddedWallet) {
          return 'Preparing transactions...';
        }
        if (operationType === 'swap_and_burn') {
          return 'Please sign BOTH transactions in your wallet';
        }
        return 'Please sign the transaction in your wallet';
      case 'swapping':
        return 'Processing swap on Solana';
      case 'burning':
        return 'Processing burn on Solana';
      case 'complete':
        return 'Complete!';
      default:
        return 'Processing';
    }
  };

  const getStepIcon = () => {
    switch (currentStep) {
      case 'preparing':
        return '🔍';
      case 'signing':
        return isEmbeddedWallet ? '⏳' : '✍️';
      case 'swapping':
        return '💱';
      case 'burning':
        return '🔥';
      case 'complete':
        return '✅';
      default:
        return '⏳';
    }
  };

  // Calculate overall progress percentage
  const getOverallProgress = () => {
    const hasSwap = operationType === 'swap_only' || operationType === 'swap_and_burn';
    const hasBurn = operationType === 'burn_only' || operationType === 'swap_and_burn';

    if (operationType === 'swap_and_burn') {
      // Combined: preparing(20), signing(30), swapping(60), burning(90), complete(100)
      switch (currentStep) {
        case 'preparing':
          return 10;
        case 'signing':
          return 25;
        case 'swapping':
          return 40 + Math.min((attemptCount / maxAttempts) * 20, 20);
        case 'burning':
          return 70 + Math.min((attemptCount / maxAttempts) * 25, 25);
        case 'complete':
          return 100;
        default:
          return 0;
      }
    } else if (hasSwap) {
      // Swap only
      switch (currentStep) {
        case 'preparing':
          return 15;
        case 'signing':
          return 30;
        case 'swapping':
          return 50 + Math.min((attemptCount / maxAttempts) * 45, 45);
        case 'complete':
          return 100;
        default:
          return 0;
      }
    } else {
      // Burn only
      switch (currentStep) {
        case 'preparing':
          return 15;
        case 'signing':
          return 30;
        case 'burning':
          return 50 + Math.min((attemptCount / maxAttempts) * 45, 45);
        case 'complete':
          return 100;
        default:
          return 0;
      }
    }
  };

  const progressPercentage = Math.round(getOverallProgress());
  const accentColor = getTitleColor();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50">
      <div
        className="relative p-8 rounded-2xl max-w-md w-full mx-4"
        style={{
          backgroundColor: 'rgba(40, 40, 40, 0.95)',
          border: `2px solid ${accentColor}`,
          boxShadow: `0 0 30px ${accentColor}40`,
        }}
      >
        {/* Animated Dumpster */}
        <div className="flex justify-center mb-6">
          <RiveDumpster width={120} height={120} isOpen={true} />
        </div>

        {/* Title */}
        <h2
          className="text-2xl font-bold text-center mb-2"
          style={{
            fontFamily: 'Outfit, sans-serif',
            color: accentColor,
            textShadow: `0 0 20px ${accentColor}80`,
          }}
        >
          {getTitle()}
        </h2>

        {/* Operation Summary */}
        {operationType === 'swap_and_burn' && (
          <div
            className="text-center text-sm mb-3 space-y-1"
            style={{
              fontFamily: 'Outfit, sans-serif',
              color: 'rgba(255, 255, 255, 0.7)',
            }}
          >
            <p>
              <span style={{ color: '#4ADE80' }}>Swapping:</span> {swapTokenCount} token
              {swapTokenCount > 1 ? 's' : ''}
            </p>
            <p>
              <span style={{ color: '#F97316' }}>Burning:</span> {totalBurnAssets} asset
              {totalBurnAssets > 1 ? 's' : ''}
            </p>
          </div>
        )}

        {/* Current Token Info (for swap) */}
        {swapCurrentSymbol && currentStep === 'swapping' && swapTokenCount > 1 && (
          <p
            className="text-center text-sm mb-2"
            style={{
              fontFamily: 'Outfit, sans-serif',
              color: 'rgba(255, 255, 255, 0.7)',
            }}
          >
            Processing {swapCurrentSymbol} ({swapCurrentIndex + 1} of {swapTokenCount})
          </p>
        )}

        {/* Estimated SOL Output */}
        {totalEstimatedSol > 0 && (
          <p
            className="text-center text-lg mb-4"
            style={{
              fontFamily: 'Outfit, sans-serif',
              color: '#FFFFFF',
            }}
          >
            Estimated:{' '}
            <span style={{ color: accentColor, fontWeight: 600 }}>
              ~{totalEstimatedSol.toFixed(6)} SOL Credits
            </span>
          </p>
        )}

        {/* Step Icon and Message */}
        <div className="flex items-center justify-center gap-3 mb-6">
          <span className="text-3xl">{getStepIcon()}</span>
          <p
            className="text-lg text-center"
            style={{
              fontFamily: 'Outfit, sans-serif',
              color: '#FFFFFF',
            }}
          >
            {getStepMessage()}
            {dots}
          </p>
        </div>

        {/* Overall Progress Bar */}
        <div className="mb-6">
          <div
            className="flex justify-between text-sm mb-2"
            style={{
              fontFamily: 'Outfit, sans-serif',
              fontWeight: 500,
              color: 'rgba(255, 255, 255, 0.8)',
            }}
          >
            <span>Progress</span>
            <span style={{ fontWeight: 600, color: accentColor }}>{progressPercentage}%</span>
          </div>
          <div
            className="h-3 rounded-full overflow-hidden relative"
            style={{ backgroundColor: 'rgba(255, 255, 255, 0.1)' }}
          >
            <div
              className="h-full transition-all duration-500 relative"
              style={{
                width: `${progressPercentage}%`,
                background:
                  operationType === 'burn_only'
                    ? 'linear-gradient(90deg, #F97316, #EA580C)'
                    : 'linear-gradient(90deg, #4ADE80, #22C55E)',
              }}
            >
              {/* Animated glow effect on progress bar */}
              <div
                className="absolute inset-0 opacity-50"
                style={{
                  background:
                    'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
                  animation: 'shimmer 2s infinite',
                }}
              />
            </div>
          </div>
        </div>

        {/* Batch Signing Info */}
        {currentStep === 'signing' && operationType === 'swap_and_burn' && !isEmbeddedWallet && (
          <div
            className="mb-4 p-3 rounded-lg text-sm"
            style={{
              fontFamily: 'Outfit, sans-serif',
              backgroundColor: 'rgba(74, 222, 128, 0.1)',
              border: '1px solid rgba(74, 222, 128, 0.3)',
              color: 'rgba(255, 255, 255, 0.9)',
            }}
          >
            <div className="flex items-start gap-2">
              <span className="text-lg">💡</span>
              <span>
                <strong>One signature, two operations!</strong> Your wallet will show both
                transactions. Sign them together to complete the swap and burn in one go.
              </span>
            </div>
          </div>
        )}

        {/* Embedded Wallet Info Banner */}
        {isEmbeddedWallet && (
          <div
            className="mb-4 p-3 rounded-lg text-sm"
            style={{
              fontFamily: 'Outfit, sans-serif',
              backgroundColor: 'rgba(74, 222, 128, 0.1)',
              border: '1px solid rgba(74, 222, 128, 0.3)',
              color: 'rgba(255, 255, 255, 0.9)',
            }}
          >
            <div className="flex items-start gap-2">
              <span>ℹ️</span>
              <span>
                Using embedded wallet. Transaction will process automatically - no signing popup
                will appear.
              </span>
            </div>
          </div>
        )}

        {/* Processing Steps */}
        <div className="space-y-3 mb-6">
          {operationType === 'swap_and_burn' ? (
            <>
              <StepIndicator
                step="1"
                label="Prepare Transactions"
                status={
                  currentStep === 'preparing'
                    ? 'active'
                    : currentStep !== 'preparing'
                      ? 'complete'
                      : 'pending'
                }
                accentColor={accentColor}
              />
              <StepIndicator
                step="2"
                label={isEmbeddedWallet ? 'Process Transactions' : 'Sign Both Transactions'}
                status={
                  currentStep === 'signing'
                    ? 'active'
                    : ['swapping', 'burning', 'complete'].includes(currentStep)
                      ? 'complete'
                      : 'pending'
                }
                accentColor={accentColor}
              />
              <StepIndicator
                step="3"
                label="Swap Tokens"
                status={
                  currentStep === 'swapping'
                    ? 'active'
                    : ['burning', 'complete'].includes(currentStep)
                      ? 'complete'
                      : 'pending'
                }
                accentColor="#4ADE80"
              />
              <StepIndicator
                step="4"
                label="Burn Assets"
                status={
                  currentStep === 'burning'
                    ? 'active'
                    : currentStep === 'complete'
                      ? 'complete'
                      : 'pending'
                }
                accentColor="#F97316"
              />
            </>
          ) : operationType === 'swap_only' ? (
            <>
              <StepIndicator
                step="1"
                label="Get Jupiter Quotes"
                status={
                  currentStep === 'preparing'
                    ? 'active'
                    : currentStep !== 'preparing'
                      ? 'complete'
                      : 'pending'
                }
                accentColor={accentColor}
              />
              <StepIndicator
                step="2"
                label={isEmbeddedWallet ? 'Prepare Transaction' : 'Sign Transaction'}
                status={
                  currentStep === 'signing'
                    ? 'active'
                    : ['swapping', 'complete'].includes(currentStep)
                      ? 'complete'
                      : 'pending'
                }
                accentColor={accentColor}
              />
              <StepIndicator
                step="3"
                label="Process Swap"
                status={
                  currentStep === 'swapping'
                    ? 'active'
                    : currentStep === 'complete'
                      ? 'complete'
                      : 'pending'
                }
                accentColor={accentColor}
              />
            </>
          ) : (
            <>
              <StepIndicator
                step="1"
                label="Prepare Transaction"
                status={
                  currentStep === 'preparing'
                    ? 'active'
                    : currentStep !== 'preparing'
                      ? 'complete'
                      : 'pending'
                }
                accentColor={accentColor}
              />
              <StepIndicator
                step="2"
                label={isEmbeddedWallet ? 'Process Transaction' : 'Sign Transaction'}
                status={
                  currentStep === 'signing'
                    ? 'active'
                    : ['burning', 'complete'].includes(currentStep)
                      ? 'complete'
                      : 'pending'
                }
                accentColor={accentColor}
              />
              <StepIndicator
                step="3"
                label="Burn Assets"
                status={
                  currentStep === 'burning'
                    ? 'active'
                    : currentStep === 'complete'
                      ? 'complete'
                      : 'pending'
                }
                accentColor={accentColor}
              />
            </>
          )}
        </div>

        {/* Error Display */}
        {error && (
          <div
            className="mb-4 p-3 rounded-lg text-sm"
            style={{
              fontFamily: 'Outfit, sans-serif',
              backgroundColor: 'rgba(239, 68, 68, 0.1)',
              border: '1px solid rgba(239, 68, 68, 0.3)',
              color: '#EF4444',
            }}
          >
            {error}
          </div>
        )}

        {/* Fun Messages */}
        <div
          className="text-center text-sm"
          style={{
            fontFamily: 'Outfit, sans-serif',
            fontWeight: 400,
            color: 'rgba(255, 255, 255, 0.5)',
          }}
        >
          {currentStep === 'preparing' && operationType === 'swap_and_burn' && (
            <p>Finding the best routes for your tokens...</p>
          )}
          {currentStep === 'swapping' && attemptCount > 5 && (
            <p>Converting your dust to SOL...</p>
          )}
          {currentStep === 'burning' && attemptCount > 5 && (
            <p>Reclaiming rent from your assets...</p>
          )}
          {attemptCount > 10 && (
            <p>Almost there! The blockchain is processing your transactions...</p>
          )}
        </div>

        {/* Source Info */}
        {operationType !== 'burn_only' && (
          <div
            className="mt-4 p-3 rounded-lg text-sm text-center"
            style={{
              fontFamily: 'Outfit, sans-serif',
              backgroundColor: 'rgba(74, 222, 128, 0.05)',
              border: '1px solid rgba(74, 222, 128, 0.2)',
              color: 'rgba(255, 255, 255, 0.6)',
            }}
          >
            Swaps powered by Jupiter Aggregator
          </div>
        )}

        {/* Warning message for slow confirmations */}
        {attemptCount > 10 && (
          <div
            className="mt-4 p-3 rounded-lg text-sm"
            style={{
              fontFamily: 'Outfit, sans-serif',
              backgroundColor: 'rgba(251, 139, 0, 0.1)',
              border: '1px solid rgba(251, 139, 0, 0.3)',
              color: '#FB8B00',
            }}
          >
            The blockchain is taking longer than usual. Your transaction is safe and will complete
            soon.
          </div>
        )}
      </div>
    </div>
  );
}

function StepIndicator({
  step,
  label,
  status,
  accentColor = '#4ADE80',
}: {
  step: string;
  label: string;
  status: 'pending' | 'active' | 'complete';
  accentColor?: string;
}) {
  return (
    <div className="flex items-center gap-3">
      <div
        className={`w-8 h-8 rounded-full flex items-center justify-center transition-all ${
          status === 'complete'
            ? 'text-white'
            : status === 'active'
              ? 'text-white animate-pulse'
              : 'bg-gray-600 text-gray-400'
        }`}
        style={{
          fontFamily: 'Outfit, sans-serif',
          fontWeight: 600,
          backgroundColor:
            status === 'complete' || status === 'active'
              ? accentColor
              : undefined,
        }}
      >
        {status === 'complete' ? '✓' : step}
      </div>
      <span
        className={`flex-1 ${
          status === 'active'
            ? 'text-white font-semibold'
            : status === 'complete'
              ? ''
              : 'text-gray-500'
        }`}
        style={{
          fontFamily: 'Outfit, sans-serif',
          color: status === 'complete' ? accentColor : undefined,
        }}
      >
        {label}
      </span>
      {status === 'active' && (
        <div
          className="w-4 h-4 border-2 border-t-transparent rounded-full animate-spin"
          style={{ borderColor: `${accentColor} transparent transparent transparent` }}
        />
      )}
    </div>
  );
}

export default CombinedDumpModal;
